import { useCallback, useEffect, useSyncExternalStore } from "react";

// Windows keep their own stacking order
let order: string[] = [];

const listeners = new Set<() => void>();

function emit() {
  for (const listener of listeners) {
    listener();
  }
}

function subscribe(listener: () => void) {
  listeners.add(listener);

  return () => {
    listeners.delete(listener);
  };
}

export function raiseWindow(id: string) {
  if (order[order.length - 1] === id) {
    return;
  }

  order = [...order.filter((entry) => entry !== id), id];
  emit();
}

function dropWindow(id: string) {
  order = order.filter((entry) => entry !== id);
  emit();
}

type WindowStack = {
  depth: number;
  raise: () => void;
};

export function useWindowDepth(id: string, open: boolean): WindowStack {
  const depth = useSyncExternalStore(
    subscribe,
    () => order.indexOf(id),
    () => -1,
  );

  useEffect(() => {
    if (!open) {
      return;
    }

    raiseWindow(id);

    return () => dropWindow(id);
  }, [id, open]);

  const raise = useCallback(() => raiseWindow(id), [id]);

  return { depth: Math.max(depth, 0), raise };
}
